import {
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  Select,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import invariant from 'tiny-invariant';
import { CombinedError, useMutation } from 'urql';
import { useTranslation } from 'react-i18next';
import {
  CalibrationUnit,
  CreateCalibrationDocument,
  EventResource,
  EventType,
  ProtanProjectStepEnum,
  ProtanProjectStepStatusEnum,
  SheetScaleTypeInput,
  UpdateCalibrationDocument,
} from '../../../gql/graphql';
import { useSheets } from '../../../hooks/sheets';
import { useSheetViewer } from '../SheetViewer';
import { useUserTenant } from '../../../services/auth-info';
import { useSheetShapes } from '../../../hooks/sheet-shapes';
import { ShapeDrawingMode } from '../../orderContractor/pdf-shapes/SheetShapeDrawing';
import { useProtanProjectSteps } from '../ProtanProjectStepsProvider';
import { SheetCalibrationButton } from './SheetCalibrationButton';
import { useEventHistory } from 'src/hooks/event-history';

export function SheetCalibration({
  projectId,
}: {
  projectId: string;
}): React.ReactElement | null {
  const {
    calibration: {
      editMode: {
        calibration: calibrationResult,
        setCalibration: setCalibrationResult,
        isActive: isCalibrationActive,
        setActive: setCalibrationActive,
      },
      calibration: existingCalibration,
    },
    pageNumber,
    activeSheetId,
  } = useSheetViewer();

  const { loading, error, sheets } = useSheets(projectId);

  const { t } = useTranslation(['common', 'project']);

  const { register, handleSubmit, reset } = useForm<{
    length: number;
    units: CalibrationUnit;
  }>();

  const [createCalibrationResult, createCalibration] = useMutation(
    CreateCalibrationDocument
  );

  const [updateCalibrationResult, updateCalibration] = useMutation(
    UpdateCalibrationDocument
  );

  const toast = useToast();
  const { tenant, isProtan } = useUserTenant();
  const {
    drawing: { activate },
  } = useSheetShapes();

  const { projectSteps, setStepStatus } = useProtanProjectSteps();

  const outLineCompleted = projectSteps?.find(
    (step) => step.step === ProtanProjectStepEnum.RoofOutline
  )?.status;

  // Effect for starting calibration step when parameter definition is completed
  useEffect(() => {
    const parameterDefinitionStatus =
      projectSteps?.find(
        (step) => step.step === ProtanProjectStepEnum.ParameterDefinition
      )?.status || ProtanProjectStepStatusEnum.NotStarted;

    const calibrationStatus =
      projectSteps?.find(
        (step) => step.step === ProtanProjectStepEnum.DrawingCalibration
      )?.status || ProtanProjectStepStatusEnum.NotStarted;

    if (
      !existingCalibration &&
      parameterDefinitionStatus === ProtanProjectStepStatusEnum.Completed &&
      calibrationStatus === ProtanProjectStepStatusEnum.NotStarted
    ) {
      setStepStatus(
        ProtanProjectStepEnum.DrawingCalibration,
        ProtanProjectStepStatusEnum.InProgress
      );
      setCalibrationActive(true);
    }
  }, [existingCalibration, projectSteps, setCalibrationActive, setStepStatus]);

  const { logEvent } = useEventHistory(projectId);

  const onSubmit = handleSubmit(async ({ length, units }) => {
    invariant(calibrationResult[0], 'Point A must be set');
    invariant(calibrationResult[1], 'Point B must be set');
    invariant(pageNumber, 'Page number must be set');
    invariant(activeSheetId, 'Active sheet must be set');

    let requestError: CombinedError | undefined = undefined;
    const calibrationInput: SheetScaleTypeInput = {
      destinationWidth: length,
      destinationUnit: units,
      pointA: {
        x: calibrationResult[0][0],
        y: calibrationResult[0][1],
      },
      pointB: {
        x: calibrationResult[1][0],
        y: calibrationResult[1][1],
      },
    };
    if (existingCalibration) {
      const { error: updateError } = await updateCalibration({
        input: {
          sheetId: activeSheetId,
          pageNumber,
          patch: {
            calibration: calibrationInput,
          },
        },
      });
      requestError = updateError;
    } else {
      const { error: createError } = await createCalibration({
        input: {
          sheetPageCalibration: {
            sheetId: activeSheetId,
            pageNumber,
            calibration: calibrationInput,
          },
        },
      });
      requestError = createError;
    }

    if (requestError) {
      toast({
        title: 'Error',
        description: requestError.message,
        status: 'error',
        isClosable: true,
      });
    } else if (isProtan) {
      setStepStatus(
        ProtanProjectStepEnum.DrawingCalibration,
        ProtanProjectStepStatusEnum.Completed
      );
      if (outLineCompleted !== ProtanProjectStepStatusEnum.Completed) {
        activate(ShapeDrawingMode.MagicPolygon);
        setStepStatus(
          ProtanProjectStepEnum.RoofOutline,
          ProtanProjectStepStatusEnum.InProgress
        );
      }
    }
    setCalibrationActive(false);
    setCalibrationResult([null, null]);

    logEvent({
      eventName: 'Calibration Updated',
      eventType: EventType.Modified,
      resourceName: 'Calibration',
      resourceType: EventResource.Sheet,
      metadata: {
        affectedResources: [activeSheetId],
      },
    });
  });

  if (loading || error) {
    return null;
  }

  const isCalibrationComplete = calibrationResult[0] && calibrationResult[1];

  const onCalibrate = () => {
    setCalibrationActive(true);
    if (existingCalibration) {
      setCalibrationResult([
        [
          existingCalibration.calibration.pointA.x,
          existingCalibration.calibration.pointA.y,
        ],
        [
          existingCalibration.calibration.pointB.x,
          existingCalibration.calibration.pointB.y,
        ],
      ]);
      reset({
        length: existingCalibration.calibration.destinationWidth,
        units: existingCalibration.calibration.destinationUnit,
      });
    } else {
      setCalibrationResult([null, null]);
    }
  };

  // Hide button if no sheets are loaded
  if (!sheets || sheets.length === 0) {
    return null;
  }

  return (
    <>
      <Popover isOpen={isCalibrationActive} closeOnBlur={false} isLazy>
        <PopoverAnchor>
          <SheetCalibrationButton
            hasCalibration={!!existingCalibration}
            onClick={onCalibrate}
          />
        </PopoverAnchor>
        <PopoverContent borderRadius={'2xl'}>
          <PopoverArrow />
          <PopoverHeader fontWeight="bold">
            {t('project:viewer-interactions.calibrate')}
          </PopoverHeader>
          <PopoverBody>
            {isCalibrationComplete ? (
              <form id="calibrate-page-form" onSubmit={onSubmit}>
                <Flex gap={2}>
                  <FormControl isRequired>
                    <FormLabel>
                      {t('project:viewer-interactions.calibration.length')}
                    </FormLabel>
                    <Input
                      size={'sm'}
                      {...register('length', {
                        valueAsNumber: true,
                      })}
                      type="number"
                      step="any"
                      min="0"
                      autoFocus
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>
                      {t('project:viewer-interactions.calibration.unit')}
                    </FormLabel>
                    <Select
                      size={'sm'}
                      {...register('units')}
                      borderRadius={'3xl'}
                    >
                      <option value={CalibrationUnit.Mm}>
                        {t('millimeters')}
                      </option>
                      <option value={CalibrationUnit.M}>{t('meters')}</option>
                    </Select>
                  </FormControl>
                </Flex>
              </form>
            ) : (
              <>{t('project:viewer-interactions.calibration.prompt')}</>
            )}
          </PopoverBody>
          <PopoverFooter>
            <Flex justifyContent={'end'} gap={2}>
              <ButtonGroup>
                <Button
                  size={'sm'}
                  colorScheme="brand"
                  type="submit"
                  form="calibrate-page-form"
                  isLoading={
                    createCalibrationResult.fetching ||
                    updateCalibrationResult.fetching
                  }
                  isDisabled={!isCalibrationComplete}
                >
                  {t('project:viewer-interactions.calibrate')}
                </Button>
                <Button
                  size={'sm'}
                  colorScheme={'gray'}
                  onClick={() => {
                    setCalibrationActive(false);
                    setCalibrationResult([null, null]);
                  }}
                >
                  {t('project:viewer-interactions.calibration.cancel')}
                </Button>
              </ButtonGroup>
            </Flex>
          </PopoverFooter>
        </PopoverContent>
      </Popover>
    </>
  );
}
